/**
 * Computes an isolated browser `autoComplete` token for a form field.
 *
 * Browsers (especially Chromium and WebKit) use cross-field heuristics to
 * autofill all matching profile fields in a form (e.g. picking a suggested
 * phone number automatically populates email, first name, and last name).
 *
 * Under the WHATWG HTML standard, specifying a unique `section-*` name on each
 * control isolates its autofill scope so the browser NEVER fills controls in
 * other sections during a single autofill action.
 */
export function deriveFieldAutoComplete(fieldName: string, type?: string): string {
  const cleanName = fieldName.replace(/[^a-zA-Z0-9_-]/g, '_');
  const leaf = fieldName.split('.').pop() ?? fieldName;
  const normalized = leaf.toLowerCase().replace(/[-_]/g, '');

  let token = 'off';
  if (type === 'email' || normalized.includes('email')) {
    token = 'email';
  } else if (
    type === 'tel' ||
    normalized.includes('phone') ||
    normalized.includes('mobile')
  ) {
    token = 'tel';
  } else if (
    normalized === 'firstname' ||
    normalized === 'givenname' ||
    normalized === 'fname'
  ) {
    token = 'given-name';
  } else if (
    normalized === 'lastname' ||
    normalized === 'familyname' ||
    normalized === 'surname' ||
    normalized === 'lname'
  ) {
    token = 'family-name';
  } else if (normalized === 'name' || normalized === 'fullname') {
    token = 'name';
  } else if (normalized.includes('address')) {
    token = 'street-address';
  }

  return token === 'off' ? 'off' : `section-${cleanName} ${token}`;
}
